// UserManagementForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Typography,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  createTheme,
  ThemeProvider,
  useTheme,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  VpnKey as VpnKeyIcon,
  Lock as LockIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { FaCheck, FaX } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';

const UserManagementForm = ({ user, onClose, onSave }) => {
  const initialFormState = {
    KullaniciAdi: '',
    Adi: '',
    Soyadi: '',
    EPosta: '',
    Tel: '',
    YetkiKodu: '',
    Sifre: '',
    SifreTekrar: '',
    Etkin: true,
    BayiId: ''
  };

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    if (user) {
      setFormData({
        KullaniciAdi: user.KullaniciAdi || '',
        Adi: user.Adi || '',
        Soyadi: user.Soyadi || '',
        EPosta: user.EPosta || '',
        Tel:user.Tel || '',
        YetkiKodu: user.YetkiKodu || '',
        Sifre: '',
        SifreTekrar: '',
        Etkin: user.Etkin ?? true,
        BayiId: user.BayiId
      });
    } else {
      setFormData(initialFormState);
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.Adi.trim() || !formData.Soyadi.trim()) {
      toast.error('Ad ve Soyad alanları boş bırakılamaz');
      return;
    }

    if (!formData.KullaniciAdi.trim()) {
      toast.error('Kullanıcı adı boş bırakılamaz');
      return;
    }

    if (!formData.EPosta.trim()) {
      toast.error('E-posta adresi boş bırakılamaz');
      return;
    }

    if (!formData.YetkiKodu) {
      toast.error('Lütfen bir yetki seviyesi seçin');
      return;
    }

    if (formData.Sifre || formData.SifreTekrar) {
      if (formData.Sifre.length < 6) {
        toast.error('Şifre en az 6 karakter olmalıdır');
        return;
      }

      if (formData.Sifre !== formData.SifreTekrar) {
        toast.error('Şifreler eşleşmiyor');
        return;
      }
    }

    const submitData = { ...formData };
    if (!submitData.Sifre) {
      delete submitData.Sifre;
      delete submitData.SifreTekrar;
    } else {
      delete submitData.SifreTekrar;
    }

    try {
      await onSave(submitData);
    } catch (error) {
      console.error('Form gönderim hatası:', error);
      toast.error('İşlem sırasında bir hata oluştu');
    }
  };

  const handleClose = () => {
    setFormData(initialFormState);
    onClose();
  };

  const outerTheme = useTheme();

  const customTheme = (outerTheme) =>
    createTheme({
      palette: {
        ...outerTheme.palette,
        mode: outerTheme.palette.mode,
      },
      typography: {
        ...outerTheme.typography,
      },
      components: {
        MuiTextField: {
          styleOverrides: {
            root: {
              "--TextField-brandBorderColor": outerTheme.palette.mode === 'dark' ? "#424242" : "#D0D0D0",
              "--TextField-brandBorderHoverColor": outerTheme.palette.mode === 'dark' ? "#616161" : "#9E9E9E",
              "--TextField-brandBorderFocusedColor": outerTheme.palette.mode === 'dark' ? "#757575" : "#616161",
              "& label.Mui-focused": {
                color: "var(--TextField-brandBorderFocusedColor)",
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              borderColor: "var(--TextField-brandBorderColor)",
            },
            root: {
              [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: "var(--TextField-brandBorderHoverColor)",
              },
              [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: "var(--TextField-brandBorderFocusedColor)",
              },
              "& input": {
                fontSize: "calc(1.7vh * 0.9)",
              },
              "& textarea": {
                fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
              },
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            root: {
              "& .MuiSelect-select": {
                fontSize: "calc(1.7vh * 0.9)",
              },
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              fontSize: "calc(1.7vh * 0.9)",
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: outerTheme.palette.mode === 'dark'
                ? "0 1px 3px rgba(0,0,0,0.3)"
                : "0 1px 3px rgba(0,0,0,0.08)",
              borderRadius: "8px",
              border: outerTheme.palette.mode === 'dark'
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(0,0,0,0.06)",
              backgroundColor: outerTheme.palette.mode === 'dark'
                ? outerTheme.palette.background.paper
                : "#FFFFFF",
            },
          },
        },
        MuiDivider: {
          styleOverrides: {
            root: {
              borderColor: outerTheme.palette.mode === 'dark'
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            },
          },
        },
      },
    });

  const SectionHeader = ({ icon: Icon, title }) => (
    <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
      <Icon
        sx={{
          color: outerTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.54)',
          fontSize: '20px'
        }}
      />
      <Typography
        variant="subtitle1"
        fontWeight={700}
        color="text.secondary"
        sx={{
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          fontSize: '0.75rem'
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  return (
    <ThemeProvider theme={customTheme(outerTheme)}>
      <Box sx={{ width: '100%' }}>
        <form id="usermanagementform" onSubmit={handleSubmit}>
          {/* Kişisel Bilgiler */}
          <Card elevation={0} sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <SectionHeader icon={PersonIcon} title="Kişisel Bilgiler" />
              <Divider sx={{ mb: 2, mt: 0.5 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Adı"
                    name="Adi"
                    value={formData.Adi}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Soyadı"
                    name="Soyadi"
                    value={formData.Soyadi}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Kullanıcı Adı"
                    name="KullaniciAdi"
                    value={formData.KullaniciAdi}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* İletişim Bilgileri */}
          <Card elevation={0} sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <SectionHeader icon={EmailIcon} title="İletişim Bilgileri" />
              <Divider sx={{ mb: 2, mt: 0.5 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="E-posta"
                    name="EPosta"
                    type="email"
                    value={formData.EPosta}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Tel"
                    name="Tel"
                    type="tel"
                    value={formData.Tel}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Yetki ve Güvenlik */}
          <Card elevation={0} sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <SectionHeader icon={SecurityIcon} title="Yetki ve Güvenlik" />
              <Divider sx={{ mb: 2, mt: 0.5 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Yetki Seviyesi</InputLabel>
                    <Select
                      name="YetkiKodu"
                      value={formData.YetkiKodu}
                      onChange={handleChange}
                      label="Yetki Seviyesi"
                      renderValue={(value) => (
                        <Box display="flex" alignItems="center" gap={1}>
                          <VpnKeyIcon color="action" fontSize="small" />
                          <span>{value === "1" ? "Admin" : value === "2" ? "User" : value === "3" ? "Viewer" : "Seçiniz"}</span>
                        </Box>
                      )}
                    >
                      <MenuItem value="1">Admin</MenuItem>
                      <MenuItem value="2">User</MenuItem>
                      <MenuItem value="3">Viewer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Şifre"
                    name="Sifre"
                    type="password"
                    value={formData.Sifre}
                    onChange={handleChange}
                    required={!user}
                    helperText={user ? "Değiştirmek istemiyorsanız boş bırakın" : ""}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Şifre Tekrar"
                    name="SifreTekrar"
                    type="password"
                    value={formData.SifreTekrar}
                    onChange={handleChange}
                    required={!user}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </form>
      </Box>
    </ThemeProvider>
  );
};

export default UserManagementForm;